import { Radio } from 'antd';
import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Div, Icon } from 'ui';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { enableFSEHide, fseManualSort, fseRowSortTypes, sortTypes } from '../../utils/app-configs';
import { determineSortIcon, sortParamToCOPSModel } from '../../utils/helpers';
import SortButton from '../../workgrid/components/sort-button';
import { viewConfig } from '../gantt/config';
import {
  clearLanesData,
  setAllTechnicians,
  setLanesInfo,
  setLanesInfoLocally,
  shouldFetchLaneData,
  shouldForceFetchGanttData,
  updateSort,
} from '../gantt/containers/gantt-actions';
import Table from '../table/table';

const HideIcon = styled.div`
  width: 25px;
  height: 25px;
  border-radius: 3px;
  border: 2px solid ${p => p.theme.colors.theme};
  background-color: ${p => p.isHidden ? '#fff' : p.theme.colors.theme};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${p => p.isHidden ? '0.5' : '1'};
  i {
    color: ${p => !p.isHidden ? '#fff' : p.theme.colors.theme};
    cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  }
`;

const Wrapper = styled.div`
  padding-left: 5px;
`;

const StyledRadio = styled(Radio)`
  display: block;
  height: 30px;
  line-height: 30px;
  margin-bottom: 8px;
`;

const StyledLabel = styled.div`
  display: inline-block;
  width: 25%;
`;

const StyledIcon = styled(Icon)`
  margin-right: 5px;
`;

const ManualSortWarning = styled(Div)`
  color: ${p => p.theme.colors.theme};
  display: block;
  width: 70%;
  margin-top: 10px;
  float: right;
`;

const Text = styled.p`
  opacity: ${p => p.isHidden ? '0.5' : '1'};
`;

const StyledRadioGroup = styled(Radio.Group)`
  width: 100%;
`;

const sort = (a, b) => {
  if (!a.isHidden && b.isHidden) {
    return -1;
  } else if (a.isHidden && !b.isHidden) {
    return 1;
  } else if ((!a.isHidden && !b.isHidden) || (a.isHidden && b.isHidden)) {
    return a.sortIndex - b.sortIndex;
  }
  return a.description?.localeCompare(b.description);
};

const RowsSidepanel = ({ intl }) => {
  const dispatch = useDispatch();
  const {
    allFSEsList,
    defaultSorting,
    rowDefinitions,
    activeSort,
    totalDataCount,
    currentPage,
  } = useSelector(state => state.gantt);
  const [cols, setCols] = useState([]);
  const [showInfo, setShowInfo] = useState(false);
  const [sortType, setSortType] = useState();
  const [prevSortType, setPrevSortType] = useState();
  const sortTypeRef = useRef();
  const currentCols = useRef();
  const firstFSE = totalDataCount ? (currentPage - 1) * viewConfig.pageSize : 0;
  const lastFSE = currentPage * viewConfig.pageSize > totalDataCount ?
    totalDataCount : currentPage * viewConfig.pageSize;

  const changeSortType = (e) => {
    sortTypeRef.current = e;
    setSortType(e);
  };

  useEffect(() => {
    // take the gantt description sorting from the url
    const activeSortDirection = sortParamToCOPSModel(activeSort);
    changeSortType(activeSortDirection?.find(a => a.sortDirection === sortTypes.manual) ?
      fseRowSortTypes.manual :
      fseRowSortTypes.auto,
    );
  }, [activeSort]);

  // reset the sorting when you change of sorting type
  useEffect(() => {
    if (sortType) {
      if (prevSortType === fseRowSortTypes.manual && sortType === fseRowSortTypes.auto) {
        dispatch(shouldForceFetchGanttData(true));
      }
      if (sortType === fseRowSortTypes.manual) {
        dispatch(updateSort(fseManualSort));
      } else if (activeSort && !activeSort.includes(sortTypes.manual)) {
        dispatch(updateSort(activeSort));
      } else {
        dispatch(updateSort(defaultSorting));
      }
      setPrevSortType(sortType);
    }
  }, [sortType]);

  useEffect(() => () => {
    if (sortTypeRef?.current === fseRowSortTypes.manual) {
      const manualCols = currentCols?.current || [];
      dispatch(setAllTechnicians(manualCols));
      dispatch(setLanesInfo(
        manualCols.slice(firstFSE, lastFSE)));
      dispatch(shouldFetchLaneData(true));
    }
  }, []);

  // Leave this as an useEffect since it will break otherwise
  useEffect(() => {
    if (
      allFSEsList?.length &&
      (!cols?.length || !isEqual(cols, allFSEsList))
    ) {
      setCols(allFSEsList?.sort(sort));
      setShowInfo(false);
    }
  }, [allFSEsList]);

  // Use ref to have the correct value on dismount
  useDeepCompareEffect(() => {
    currentCols.current = cols;
  }, [cols]);

  const handleRowVisibilityChange = (row) => {
    let hiddenElements = cols?.filter(f => f.isHidden && f.id !== row.id);
    let visibleElements = cols
      ?.sort((a, b) => a.sortIndex - b.sortIndex)
      ?.filter(f => !f.isHidden && f.id !== row.id);

    if (row.isHidden) {
      const last = visibleElements[visibleElements.length - 1];

      visibleElements = [
        ...visibleElements,
        {
          ...row,
          sortIndex: last ? last.sortIndex + 1 : 0,
          isHidden: false,
        },
      ];
    } else {
      hiddenElements = [
        ...hiddenElements,
        {
          ...row,
          sortIndex: hiddenElements.length,
          isHidden: true,
        },
      ]?.sort((a, b) => a.description?.localeCompare(b.description));
    }
    visibleElements = visibleElements?.sort((a, b) => a.sortIndex - b.sortIndex);

    const data = [...visibleElements, ...hiddenElements]?.map((d, i) => ({
      ...d,
      sortIndex: i,
    }));
    setCols(data);
    dispatch(setLanesInfoLocally(data.slice(firstFSE, lastFSE)));
    dispatch(clearLanesData());
  };

  const handleDrop = (e) => {
    // MW is not able to change the 'sortIndex' prop to 'index', we need to formatted it after drop
    const list = e?.map(item => ({
      ...item,
      sortIndex: item?.index,
    }))?.sort(sort);
    setCols(list);
    setShowInfo(true);
    dispatch(setLanesInfoLocally(list.slice(firstFSE, lastFSE)));
    dispatch(clearLanesData());
  };

  const sortingColumns = [
    {
      id: 'row',
      displayName: <FormattedMessage id='worklist.filters.ROW' />,
      renderFn: row => <Text isHidden={row.isHidden}>{row.rowName}</Text>,
    },
    {
      id: 'sorting',
      displayName: <>
        <div><FormattedMessage id='worklist.filters.SORTING' /></div>
      </>,
      renderFn: row => row.sortable ?
        <SortButton
          disabled={sortType === fseRowSortTypes.manual}
          id={row.id}
          icons={row.icons}
          activeSort={activeSort}
          updateCb={updateSort}
          defaultSorting={defaultSorting}
        /> :
        null,
    },
  ];

  const fieldEngineersDataColumns = [{
    id: 'name',
    displayName: <FormattedMessage id='worklist.filters.FIELD_ENGINEERS' />,
    renderFn: row => <Text isHidden={row.isHidden}>{row.description}</Text>,
  }];

  const fieldEngineersColumns = enableFSEHide ? [
    {
      id: 'showhide',
      displayName: '',
      width: '50px',
      renderFn: row => <HideIcon
        disabled={sortType === fseRowSortTypes.auto}
        isHidden={row.isHidden}
        onClick={() =>
          sortType === fseRowSortTypes.manual && handleRowVisibilityChange(row)
        }
      >
        <Icon className={`fa-solid fa-eye${row.isHidden ? '-slash' : ''}`} size='sm' />
      </HideIcon>,
    },
    ...fieldEngineersDataColumns,
  ] : fieldEngineersDataColumns;

  return (
    <Wrapper>
      <StyledRadioGroup
        onChange={(e) => {
          changeSortType(e.target.value);
        }}
        value={sortType}
      >
        <StyledRadio value={fseRowSortTypes.auto}>
          {intl.formatMessage({
            id: 'worklist.filters.SORT_AUTOMATICALLY',
          })}
        </StyledRadio>
        <Table
          disabled={sortType === fseRowSortTypes.manual}
          columns={sortingColumns}
          data={rowDefinitions?.map(def => ({
            rowName: def?.displayName,
            id: def?.id,
            sortable: def?.sortable,
            sortIndex: def?.sort,
            icons: {
              sortAscending: determineSortIcon(def.id, def.type, true),
              sortDescending: determineSortIcon(def.id, def.type, false),
            },
          }))}
          lightHeader={true}
        />
        <StyledRadio value={fseRowSortTypes.manual}>
          <StyledLabel>
            {intl.formatMessage({
              id: 'worklist.filters.SORT_MANUALLY',
            })}
          </StyledLabel>
          {showInfo &&
            <ManualSortWarning>
              <StyledIcon type='info-circle' />
              {`(${intl.formatMessage({
                id: 'worklist.filters.GANTT_REFRESH_WARNING',
              })})`}
            </ManualSortWarning>
          }
        </StyledRadio>
        <Table
          disabled={sortType === fseRowSortTypes.auto}
          columns={fieldEngineersColumns}
          data={cols?.map((row, i) => ({
            ...row,
            index: i,
          }))}
          lightHeader={true}
          movableRows={true}
          onDrop={handleDrop}
        />
      </StyledRadioGroup>
    </Wrapper>
  );
};

RowsSidepanel.propTypes = {
  intl: PropTypes.object,
};

export default injectIntl(RowsSidepanel);
